import { useRef, useState } from "react";
import { actions } from "../store";
import type { JobCardModel } from "../types";

// PDF/DOCX parsers are heavy; load them only when a file is actually uploaded.
const ACCEPTED_TYPES = ".pdf,.docx,.txt,.md";

interface PendingDiff {
  jobId: string;
  jobTitle: string;
  index: number;
  original?: string;
  suggested: string;
  reason: string;
}

export function ResumeCanvas({
  resume,
  jobs,
}: {
  resume: string;
  jobs: JobCardModel[];
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { parseResumeFile } = await import("../lib/resumeParser");
      const text = await parseResumeFile(file);
      actions.setResume(text);
      actions.noteSystem(`Loaded resume from ${file.name}.`, "ok");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not read file.";
      setUploadError(message);
      actions.noteSystem(`Resume upload failed: ${message}`, "error");
    } finally {
      setUploading(false);
    }
  }

  // Gather every un-applied bullet suggestion across matched jobs.
  const pending: PendingDiff[] = jobs.flatMap((j) =>
    (j.match?.bulletDiffs ?? []).map((d, index) => ({
      jobId: j.id,
      jobTitle: j.title,
      index,
      original: d.original,
      suggested: d.suggested,
      reason: d.reason,
    })),
  );

  return (
    <div className="panel flex min-h-0 flex-1 flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="font-display text-sm font-medium tracking-wide">Resume Canvas</h2>
          <p className="mt-0.5 truncate text-[11px] text-faint">
            Upload your resume or edit below. Accept agent suggestions to merge.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <span className="hidden font-mono text-[10px] text-faint sm:inline">
            {resume.trim() ? `${resume.trim().split(/\s+/).length} words` : "empty"}
          </span>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={onFile}
            className="hidden"
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="btn-ghost px-2.5 py-1.5 text-xs"
            title="Upload a PDF, DOCX, or TXT resume"
          >
            {uploading ? (
              <>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-muted/40 border-t-muted" />
                Reading…
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 16V4m0 0L7 9m5-5l5 5M5 20h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Upload
              </>
            )}
          </button>
        </div>
      </header>
      {uploadError && (
        <p className="border-b border-border bg-rose/10 px-4 py-2 text-[11px] text-rose">
          {uploadError}
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
        <textarea
          value={resume}
          onChange={(e) => actions.setResume(e.target.value)}
          spellCheck={false}
          className="scroll-slim min-h-[180px] flex-1 resize-none rounded-xl border border-border
            bg-ink/50 p-4 font-mono text-[12.5px] leading-relaxed text-text
            placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/50"
          placeholder="Paste your resume here…"
        />

        <div className="shrink-0">
          <div className="mb-2 flex items-center justify-between">
            <span className="eyebrow">Tailored suggestions</span>
            {pending.length > 0 && (
              <span className="rounded-full bg-accent-soft px-2 py-0.5 font-mono text-[10px] text-accent">
                {pending.length} pending
              </span>
            )}
          </div>

          {pending.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border/70 px-3 py-4 text-center text-[11px] leading-relaxed text-faint">
              Run “match” on a job (or let the agent call{" "}
              <code className="font-mono text-muted">match_with_resume</code>) to get
              tailored bullet rewrites for this resume.
            </p>
          ) : (
            <div className="scroll-slim flex max-h-[34vh] flex-col gap-2 overflow-y-auto pr-1">
              {pending.map((d) => (
                <DiffChip key={`${d.jobId}-${d.index}`} diff={d} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffChip({ diff }: { diff: PendingDiff }) {
  return (
    <div className="animate-slide-in rounded-xl border border-border bg-surface-2/70 p-3">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="rounded bg-accent-soft px-1.5 py-0.5 font-mono text-[10px] text-accent">
          {diff.original ? "rewrite" : "new bullet"}
        </span>
        <span className="truncate text-[11px] text-faint">for {diff.jobTitle}</span>
      </div>

      {diff.original && (
        <p className="mb-1 text-[12px] leading-relaxed text-faint line-through decoration-rose/50">
          {diff.original}
        </p>
      )}
      <p className="text-[12.5px] leading-relaxed text-text">
        <span className="mr-1 text-signal">+</span>
        {diff.suggested}
      </p>
      <p className="mt-1.5 text-[11px] italic leading-relaxed text-muted">{diff.reason}</p>

      <div className="mt-2.5 flex items-center gap-2">
        <button
          onClick={() => actions.applyDiff(diff.jobId, diff.index)}
          className="rounded-md bg-accent px-2.5 py-1 text-[11px] font-medium text-white hover:bg-accent-dim"
        >
          Accept
        </button>
        <button
          onClick={() => actions.dismissDiff(diff.jobId, diff.index)}
          className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted hover:border-border-bright hover:text-text"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
